import { useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  extractAdminPortalMessage,
  useAdminModulePermission,
} from "../../../../utils/adminPortalHelpers";
import { showErrorMsg, showSuccessMsg } from "../../../../utils/Helpers";
import { createAdminCompanyClient } from "../../../../network/apis/adminCompanyClients";
import { buildCreateCompanyClientPayload } from "../../../Company-portal/Clients/clientMappers";
import {
  adminCompanyClientRoutes,
  COMPANY_DETAILS_CLIENTS_TAB,
} from "../adminCompanyClientRoutes";

const useAdminAddClient = () => {
  const history = useHistory();
  const { companyId } = useParams();
  const lang = useSelector((state) => state.locale.lang);
  const { clients, shared, inputsValidations } = messages[lang];
  const companiesList = messages[lang].companiesList;
  const addClientCopy = clients.addClient;
  const { canAdd } = useAdminModulePermission(
    companyPermissionModules.manageClients,
  );

  const [creating, setCreating] = useState(false);

  const goBackToList = useCallback(() => {
    if (!companyId) {
      history.push(ROUTE_PATHS.companiesList);
      return;
    }
    history.push(
      adminCompanyClientRoutes.companyDetailsTabLocation(
        companyId,
        COMPANY_DETAILS_CLIENTS_TAB,
      ),
    );
  }, [history, companyId]);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      phoneCountryDialCode: "966",
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required("nameRequired"),
      email: Yup.string()
        .trim()
        .email("validEmailFormat")
        .required("emailRequired"),
      phone: Yup.string().nullable(),
    }),
    onSubmit: async (values) => {
      if (!companyId || creating) return;

      const data = {
        ...buildCreateCompanyClientPayload(values),
        company_id: companyId,
      };

      setCreating(true);
      try {
        const response = await createAdminCompanyClient(data);
        const message = extractAdminPortalMessage(response);
        if (message) showSuccessMsg(message, true);
        else showSuccessMsg(addClientCopy.createSuccess || "addedSuccessfully");
        goBackToList();
      } catch (error) {
        showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
      } finally {
        setCreating(false);
      }
    },
  });

  const breadcrumbList = useMemo(
    () => [
      { path: ROUTE_PATHS.companiesList, label: companiesList.listBreadcrumb },
      {
        path: companyId
          ? adminCompanyClientRoutes.companyDetailsClientsTab(companyId)
          : "",
        label: companiesList.clientsTab,
      },
      { path: "", label: addClientCopy.addNewClientTitle },
    ],
    [
      companiesList.listBreadcrumb,
      companiesList.clientsTab,
      addClientCopy.addNewClientTitle,
      companyId,
    ],
  );

  const handleCancel = useCallback(() => {
    goBackToList();
  }, [goBackToList]);

  return {
    clients,
    shared,
    inputsValidations,
    breadcrumbList,
    creating,
    canAdd,
    formik,
    onCancel: handleCancel,
  };
};

export default useAdminAddClient;
